import React, { useState, useRef, useEffect } from "react";
import { Bot, Send, X, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Apex Sentinel online. Ask about a storm threat, crew capacity, a claim, or run a damage scenario — I'll surface the signal.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/send", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `[ error · ${formatApiError(err.response?.data?.detail) || err.message} ]` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 px-5 py-3 btn-primary-apex font-mono-apex text-[11px] uppercase tracking-[0.25em] shadow-[0_0_30px_rgba(220,38,38,0.4)]"
          data-testid="chat-widget-toggle"
        >
          <Bot size={16} strokeWidth={1.5} />
          Sentinel
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-md h-[560px] flex flex-col border border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl shadow-2xl shadow-black/60"
          data-testid="chat-widget-panel"
        >
          <div className="h-14 px-5 flex items-center justify-between border-b border-white/5">
            <div className="flex items-center gap-2.5">
              <span className="live-dot" />
              <div>
                <div className="font-display text-base text-white leading-none">Apex Sentinel</div>
                <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">claude · sonnet 4.5</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="chat-widget-close">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 scroll-thin">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-3 ${
                    m.role === "user"
                      ? "bg-[#dc2626] text-white"
                      : "bg-[#121214] border border-white/5 text-zinc-100"
                  } font-${m.role === "assistant" ? "mono-apex text-[12px] leading-relaxed" : "body text-sm"}`}
                >
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-[#121214] border border-white/5 font-mono-apex text-xs text-zinc-400 flex items-center gap-2">
                  <Sparkles size={12} className="animate-pulse text-[#dc2626]" /> reasoning…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-white/5 p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Sentinel…"
              className="input-apex flex-1 px-4 py-3 font-body text-sm"
              data-testid="chat-widget-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="btn-primary-apex p-3 disabled:opacity-50"
              data-testid="chat-widget-send"
            >
              <Send size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
